import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { ClientError, GraphQLClient, RequestDocument } from 'graphql-request'
import { cookieHandler, dustinAuth } from '@dustin-web/auth-js'
import { MicrositeSettings } from '@dustin-web/microsite-config'
import { constants } from '@dustin-web/microsite-config/src/constants'
import { createPersistedQueryFetch } from '../graphql-request/persisted-query'

export type GraphqlContext = {
  cacheKey?: string
  headers?: Record<string, string>
  keepPreviousResult?: boolean
  skip?: boolean
  verbose?: boolean
}

export const DEFAULT_GRAPHQL_CONTEXT: GraphqlContext = {
  keepPreviousResult: true,
}

const isServer = typeof window === 'undefined'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isUnauthorizedStatus = (err: any) => err?.statusCode === 401 || err?.response?.status === 401

export type GraphqlFetcherWithCacheKey = ReturnType<typeof getGraphqlFetcherWithCacheKey>

const getOperationName = (document: RequestDocument) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const valueObject = document.valueOf() as string | (object & { definitions: any[] })
  return typeof valueObject === 'string'
    ? valueObject
    : valueObject?.definitions?.find(d => d.kind === 'OperationDefinition')?.name?.value
}

// Only queries may fall back to anonymous. Like the persisted-query adapter, a string document
// that mentions "mutation" or "subscription" anywhere counts as one, so anything unsure keeps
// the login redirect.
const isQueryOperation = (document: RequestDocument) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const valueObject = document.valueOf() as string | (object & { definitions: any[] })
  if (typeof valueObject === 'string') {
    return !/\b(mutation|subscription)\b/.test(valueObject)
  }
  const operations = valueObject?.definitions?.filter(d => d.kind === 'OperationDefinition') ?? []
  return operations.length > 0 && operations.every(d => d.operation === 'query')
}

export const getCacheKeyFromDocument = <Variables>(
  document: RequestDocument,
  variables?: Variables
) => {
  const operationName = getOperationName(document) || 'UKNOWN_OPERATION'
  const key = variables ? ':' + JSON.stringify(variables) : ''

  return `${operationName}${key}`.replace(/"([^"]+)":/g, '$1:')
}

export type GraphqlFetcher = <TQuery = unknown, TVariables = Record<string, unknown>>(
  document: RequestDocument | TypedDocumentNode<TQuery, TVariables>,
  variables?: TVariables,
  onError?: (error: ClientError) => void
) => Promise<TQuery>

export type FetcherWithCacheKeyResult<TQuery> = Promise<[string, TQuery]>
export const getGraphqlFetcherWithCacheKey = (
  micrositeSettings: MicrositeSettings,
  cookieHeader?: string
) => {
  const fetcherWithCacheKey = async <TQuery = unknown, TVariables = Record<string, unknown>>(
    document: RequestDocument | TypedDocumentNode<TQuery, TVariables>,
    {
      variables,
      context,
    }: {
      variables?: TVariables
      context?: GraphqlContext
    } = {}
  ): FetcherWithCacheKeyResult<TQuery> => {
    if (context?.skip) {
      return ['', {} as TQuery]
    }
    const contextWithDefaults = { ...DEFAULT_GRAPHQL_CONTEXT, ...context }

    const fetcher = dangerously_getGraphqlFetcher(
      micrositeSettings,
      cookieHeader,
      contextWithDefaults
    )
    const cacheKey = context?.cacheKey || getCacheKeyFromDocument(document, variables)

    const request = fetcher.fetchGraphql<TQuery, TVariables>(document, variables)

    return Promise.all([cacheKey, request])
  }

  return fetcherWithCacheKey
}

export const dangerously_getGraphqlFetcher = (
  micrositeSettings: MicrositeSettings,
  cookieHeader = '',
  context: GraphqlContext,
  usePersistedQueries = false
) => {
  const authClient = dustinAuth(
    isServer ? micrositeSettings.authServerConfig : micrositeSettings.authConfig,
    cookieHeader
  )

  const { dustinHeaders, graphqlServerUri, graphqlUri } = micrositeSettings.graphqlConfig
  const dustinGraphqlUri = isServer ? graphqlServerUri : graphqlUri

  const headers = isServer ? { ...dustinHeaders, cookie: cookieHeader } : dustinHeaders

  // The gateway only reads the access token cookie when the request opts in (TH-2143). Only a
  // same-origin browser POST can: SSR forwards the cookie and must stay anonymous when it has
  // expired, persisted queries go over GET, and a cross-origin request carries no cookie.
  const canOptInToAccessTokenCookie =
    !isServer &&
    !usePersistedQueries &&
    new URL(dustinGraphqlUri, window.location.href).origin === window.location.origin

  // Auth headers for one attempt, passed per request: concurrent calls on one fetcher share no
  // header state. The Bearer wins at the gateway. Without one, the request opts in only when there
  // is no readable access token cookie, so it may be httpOnly; a readable but expired one stays
  // anonymous as before.
  const authHeaders = (token: string | null): Record<string, string> => {
    if (token) {
      return { authorization: `Bearer ${token}` }
    }
    if (canOptInToAccessTokenCookie && !cookieHandler().getAccessToken()) {
      return { [constants.dustinHeaders.useAccessTokenCookie]: 'true' }
    }
    return {}
  }

  // A copy, never changed: dustinHeaders is shared through micrositeSettings.
  const client = new GraphQLClient(dustinGraphqlUri, {
    headers: { ...headers },
    // Every operation goes over POST. GraphQL over GET makes a query a
    // CORS-simple request, so any site can call the gateway cross-origin
    // without a preflight. The gateway rejects GET from production, see
    // TH-2246.
    method: 'POST',
    credentials: 'same-origin',
    fetch: isServer || !usePersistedQueries ? fetch : createPersistedQueryFetch(fetch),
  })

  const fetchGraphql: GraphqlFetcher = async (document, variables, onError) => {
    const firstAttempt = authHeaders(await authClient.getAccessToken())
    const optedIn = constants.dustinHeaders.useAccessTokenCookie in firstAttempt

    client.setEndpoint(dustinGraphqlUri)

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const result = await client.request(document, variables, firstAttempt).catch(async err => {
        if (!optedIn || !isUnauthorizedStatus(err)) {
          throw err
        }
        // The gateway rejected the access token cookie: refresh it once and retry. The renewed
        // cookie may be readable (Bearer) or httpOnly (opt-in again).
        if (await authClient.refreshSession()) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return client.request(document, variables, authHeaders(await authClient.getAccessToken()))
        }
        // The cookie can't be renewed. A query is retried once anonymously, as without the
        // opt-in, so a public page still renders; an operation that needs a user gets the
        // gateway's authorization error and the login redirect below. Mutations keep the 401.
        if (isQueryOperation(document)) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return client.request(document, variables, {})
        }
        throw err
      })

      const verbose = context.verbose === false ? false : true

      if (isServer && process.env.NODE_ENV !== 'production' && verbose) {
        import('@dustin-web/microsite-config/src/logger').then(({ log }) => {
          const operationName = getOperationName(document)
          log.info({ variables }, `Network request: ${operationName}`)
        })
      }

      return result
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (isServer) {
        import('@dustin-web/microsite-config/src/logger').then(({ log }) => {
          const operationName = getOperationName(document)
          const debug = process.env.NODE_ENV !== 'production' ? { variables, err } : {}
          log.warn({ operationName, ...debug }, `GraphQL ssr error: ${operationName}`)
        })

        throw new Error(JSON.stringify(err))
      } else {
        console.error('Graphql browser error', { err })

        if (onError) {
          onError(err as ClientError)
        } else if (
          err?.statusCode === 401 ||
          err?.response?.status === 401 ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          err?.response?.errors?.some((err: any) => err.extensions?.code === 'UNAUTHORIZED') ||
          err?.response?.errors?.some((err: any) => err.extensions?.code === 'AUTH_NOT_AUTHORIZED')
        ) {
          window.location.href = `/login?returnUrl=${window.location.href}`
        }
      }

      return err?.response?.data
    }
  }

  // return { fetchGraphql, batchFetchGraphql }
  return { fetchGraphql }
}
